import { Head } from '@inertiajs/react'
import type { Establishment } from '@/types'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { EstablishmentIcon } from '@/components/nav/icons'

type Labels = {
  title: string
  add_button: string
  empty_state: string
  empty_body: string
}

type Props = {
  establishments: Establishment[]
  labels: Labels
}

function EmptyState({ labels }: { labels: Labels }) {
  return (
    <div className="mt-8 rounded-2xl border border-line bg-card px-6 py-14 text-center shadow-soft">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-mustard-tint text-mustard-deep">
        <EstablishmentIcon className="h-7 w-7" />
      </span>
      <h2 className="mt-5 font-display text-2xl text-ink">{labels.empty_state}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-ink-soft">{labels.empty_body}</p>
      <Button href="/establishments/new" className="mt-6">
        ＋ {labels.add_button}
      </Button>
    </div>
  )
}

export default function EstablishmentsIndex({ establishments, labels }: Props) {
  return (
    <>
      <Head title={labels.title} />
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4">
          <h1 className="font-display text-3xl text-ink">{labels.title}</h1>
          {establishments.length > 0 && (
            <Button href="/establishments/new" className="shrink-0">
              ＋ {labels.add_button}
            </Button>
          )}
        </div>

        {establishments.length === 0 ? (
          <EmptyState labels={labels} />
        ) : (
          <Card className="mt-6 overflow-hidden">
            <ul>
              {establishments.map((establishment) => {
                const contact = establishment.contact_email ?? establishment.contact_phone
                return (
                  <li key={establishment.id}>
                    <div className="flex items-center gap-4 border-t border-line px-5 py-4 first:border-t-0">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-mustard-tint text-mustard-deep">
                        <EstablishmentIcon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-extrabold text-ink">{establishment.name}</p>
                        {establishment.address && <p className="truncate text-sm text-ink-soft">{establishment.address}</p>}
                      </div>
                      {contact && <span className="hidden shrink-0 text-sm text-ink-faint sm:block">{contact}</span>}
                    </div>
                  </li>
                )
              })}
            </ul>
          </Card>
        )}
      </div>
    </>
  )
}
